import { RootState } from '@share/utils';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { roomsActions, ILoginState, IRoomsState } from '@share/store/slices';

import { GreenButton } from '@share/components';

import { CloseSvg } from '@share/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus';

import { IRoom } from '@share/common-types';
import { IActivityAgeBand, IAgeRange, getRoomPickerAgeConfig } from '@common-types';
import { Room } from '../room';

import './style.scss';
interface IProps {
  isUpdateSearch?: boolean;
  onSelect?: () => void;
  /** When set (e.g. from activity details), restricts ages; minAge >= 18 hides children. */
  ageRange?: IAgeRange;
  /** When set from activity details, can override child/adult bounds. */
  ageBands?: IActivityAgeBand[] | null;
}

export const ActivityRoomPicker: React.FC<IProps> = ({ isUpdateSearch, onSelect, ageRange, ageBands }) => {
  const dispatch = useDispatch();

  const roomsStore: IRoomsState = useSelector((State: RootState) => State.roomsStore);
  const loginStore: ILoginState = useSelector((State: RootState) => State.loginStore);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [changed, setChanged] = useState(false);
  const [localRooms, setLocalRooms] = useState<IRoom[]>(roomsStore.roomsSelected);

  const room = localRooms[0];

  const ageConfig = useMemo(
    () => getRoomPickerAgeConfig(ageRange, ageBands),
    [ageRange, ageBands]
  );
  const { minAdultAge, maxAdultAge, childMinAge, childMaxAge, showChildren: showChildrenInConfig } = ageConfig;

  const focus = () => {
    const fromStore = roomsStore.roomsSelected;
    const clamped = fromStore.map((r) => {
      const ages = r.adultAges ?? Array(r.adultsCount).fill(minAdultAge);
      const clampedAges = ages.slice(0, r.adultsCount).map((a: number | null) => {
        if (a == null) {
          return minAdultAge;
        }
        const c = Math.max(minAdultAge, a);
        return maxAdultAge != null ? Math.min(maxAdultAge, c) : c;
      });
      const clampedKids =
        showChildrenInConfig && r.kids?.length
          ? r.kids.map((k) => ({
              ...k,
              age: Math.min(
                childMaxAge,
                Math.max(childMinAge, k.age ?? childMinAge)
              ),
            }))
          : [];
      return { ...r, adultAges: clampedAges, kids: clampedKids };
    });
    setLocalRooms(clamped);
    setIsFocused(true);
  };

  const blur = useCallback(() => {
    if (isFocused) {
      setIsFocused(false);
      if (changed) onSelect?.();
    }
  }, [isFocused, changed, onSelect]);

  /**
   * Dismisses the picker on a click anywhere else.
   *
   * Asked for on top of the activities front, where this handler exists but can never
   * fire: `__fixed-bg` covers the viewport and is rendered inside `wrapperRef`, so it,
   * not the page behind it, is what an "outside" click lands on and `contains` is
   * always true. Treating the backdrop as outside is what makes the handler work, and
   * matches how the locations picker next to it already closes.
   *
   * Dismissing discards pending edits, exactly like the X - only Done applies them.
   */
  const handleOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (
        !wrapperRef.current?.contains(target) ||
        target.classList.contains('activity-room-picker__fixed-bg')
      ) {
        blur();
      }
    },
    [blur]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [handleOutside]);

  const isAdultOnlyActivity = !showChildrenInConfig;

  const hasInvalidAdultAges =
    room?.adultAges?.some(
      (age: number | null) =>
        age == null ||
        age < minAdultAge ||
        (maxAdultAge != null && age > maxAdultAge)
    ) ?? false;

  const hasInvalidChildAges =
    showChildrenInConfig &&
    (room?.kids?.some(
      (k) => k.age == null || k.age < childMinAge || k.age > childMaxAge
    ) ?? false);

  const hasInvalidAges = hasInvalidAdultAges || hasInvalidChildAges;

  const onDone = () => {
    if (!room || hasInvalidAges) return;

    const toApply = isAdultOnlyActivity
      ? localRooms.map((r) => ({ ...r, kids: [] }))
      : localRooms;
    dispatch(roomsActions.setRooms(toApply));
    setChanged(true);
    blur();
  };

  const onDoneClick = () => {
    if (!hasInvalidAges) onDone();
  };

  const account = loginStore?.account;
  const styleColor = account?.searchIconsColor ? { color: account.searchIconsColor } : {};

  return (
    <div className={`activity-room-picker`} ref={wrapperRef}>
      <div
        className={`activity-room-picker__wrapper ${isFocused ? "selected" : ""}`}
        onClick={focus}
      >
        <div className="activity-room-picker__left">
          <div className="activity-room-picker__icon" style={styleColor}>
            <FontAwesomeIcon icon={faUserPlus} />
          </div>
        </div>

        <div className="activity-room-picker__right">
          <div className="activity-room-picker__label">
            <FormattedMessage id="guest.title" />
          </div>

          <div className="activity-room-picker__header">
            {roomsStore.roomsSelected?.[0]?.adultsCount ?? 0} Adults
            {(roomsStore.roomsSelected?.[0]?.kids?.length ?? 0) > 0 && `, ${roomsStore.roomsSelected[0].kids?.length} Children`}
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isFocused && (
        <div className="activity-room-picker__dropdown">
          <div className="activity-room-picker__close-icon" onClick={blur}>
            <CloseSvg />
          </div>

          <Room
            room={room}
            index={0}
            haveScroll={false}
            ageRange={ageRange}
            ageBands={ageBands}
            addKid={(roomId, kid) => {
              const newKid = {
                ...kid,
                age: kid.age != null ? Math.min(childMaxAge, Math.max(childMinAge, kid.age)) : childMinAge,
              };
              setLocalRooms(prev =>
                prev.map(r =>
                  r.id === roomId ? { ...r, kids: [...(r.kids || []), newKid] } : r
                )
              );
            }}
            removeKid={(roomId, kidId) => {
              setLocalRooms(prev => prev.map(r => r.id === roomId ? { ...r, kids: (r.kids || []).filter(k => k.id !== kidId) } : r));
            }}
            updateAdultsCount={(roomId, count) => {
              setLocalRooms(prev => prev.map(r => {
                if (r.id !== roomId) return r;
                const oldCount = r.adultsCount;
                let newAdultAges = [...(r.adultAges ?? [])];
                if (count > oldCount) {
                  newAdultAges = [...newAdultAges, ...Array(count - oldCount).fill(minAdultAge)];
                } else if (count < oldCount) {
                  newAdultAges = newAdultAges.slice(0, count);
                }
                return { ...r, adultsCount: count, adultAges: newAdultAges, kids: count === 0 ? [] : r.kids };
              }));
            }}
            updateAdultAge={(roomId, AdultIndex, age: number | null) => {
              setLocalRooms(prev =>
                prev.map(r => {
                  if (r.id !== roomId) return r;
                  const newAges = [...(r.adultAges ?? [])];
                  newAges[AdultIndex] = age;
                  return { ...r, adultAges: newAges };
                })
              );
            }}
            updateKidAge={(roomId, kidId, age: number | null) => {
              const clampedAge =
                age != null
                  ? Math.min(childMaxAge, Math.max(childMinAge, age))
                  : age;
              setLocalRooms(prev =>
                prev.map(r => {
                  if (r.id !== roomId) return r;
                  return {
                    ...r,
                    kids: (r.kids || []).map((k) =>
                      k.id === kidId ? { ...k, age: clampedAge ?? childMinAge } : k,
                    ),
                  };
                })
              );
            }}
            removeRoom={() => null}
          />


          <div className="activity-room-picker__footer">
            <GreenButton onClick={onDoneClick} disabled={hasInvalidAges}>
              {!isUpdateSearch ? (
                <FormattedMessage id="done" />
              ) : (
                <FormattedMessage id="search" />
              )}
            </GreenButton>
          </div>
        </div>
      )}

      {isFocused && <div className="activity-room-picker__fixed-bg" />}
    </div>
  );
};

